import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Reveal from '../Components/Reveal';
import '../styles/Home.css';

import devVideo from '../Assets/3d model.mp4';
import cyberImg from '../Assets/cybersecurity1.jpg';
import simulationImg from '../Assets/hsse.jpg';
import aiCapImg from '../Assets/Futuristic robotic hand holding AI letters.png';
import xrImg from '../Assets/fusionsite.png';
import holoxrImg from '../Assets/Holoxr1.png';
import eduxrImg from '../Assets/eduXR1.jpg';
import mnimizeImg from '../Assets/mnmize interior.png';
import nordyneImg from '../Assets/norodyne defense dynamics.png';
import metriksImg from '../Assets/metriks data center.png';
import quolegalImg from '../Assets/Quo legal firm.png';
import agrimaxImg from '../Assets/agrimax.png';
import vintageImg from '../Assets/vintage studio.png';

/* ── Capability data ─────────────────────────────────────── */
const CAPABILITIES = {
  ai: {
    title: 'AI & Machine Learning',
    label: 'Predictive Intelligence',
    desc: 'Data-driven intelligence powering predictive analytics and automation. We transform raw information into predictive workflows through advanced learning models.',
    points: ['Neural Network Architecture', 'Predictive Asset Maintenance', 'Autonomous Optimization', 'Advanced Analytics'],
    img: aiCapImg,
  },
  xr: {
    title: 'Extended Reality',
    label: 'Immersive Visualization',
    desc: 'AR/VR/XR environments built for training, simulation, and real-time collaboration. We bridge the gap between technical data and human spatial intuition.',
    points: ['Multi-user VR Sims', 'AR Maintenance Overlays', 'Metaverse Campus', 'Real-time Interaction'],
    img: 'https://images.unsplash.com/photo-1617802690992-15d93263d3a9?w=800&auto=format&fit=crop',
  },
  dt: {
    title: 'Digital Twin Systems',
    label: 'Operational Reflection',
    desc: 'Interactive 3D environments that replicate physical assets and operations. Powered by real-time IoT feeds and predictive logic.',
    points: ['SCADA Integration', '4D Visualization', 'Remote Collaborative Ops', 'Asset Monitoring'],
    img: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&auto=format&fit=crop',
  },
  iot: {
    title: 'IoT & Networking',
    label: 'Connected Infrastructure',
    desc: 'The foundation of connectivity. We deploy industrial sensors and robust networking to ensure intelligence is always powered by a live digital thread.',
    points: ['Industrial Networking', 'Smart IoT Deployment', 'Edge Computing', 'Data Management'],
    img: 'https://images.unsplash.com/photo-1558346490-a72e53ae2d4f?w=1000&auto=format&fit=crop',
  },
  training: {
    title: 'Training & Simulation',
    label: 'Immersive Learning',
    desc: 'Redefining safety and skill acquisition. Immersive XR environments that simulate high-risk real-world scenarios safely and effectively.',
    points: ['HSSE VR Modules', 'Interactive Learning', 'Turbine Components', 'Skill Retention'],
    img: 'https://images.unsplash.com/photo-1626379953822-baec19c3bbcd?w=1000&auto=format&fit=crop',
  },
};

const PILLS = [
  { id: 'all',      label: 'All Capabilities' },
  { id: 'ai',       label: 'AI & ML' },
  { id: 'xr',       label: 'Extended Reality' },
  { id: 'dt',       label: 'Digital Twins' },
  { id: 'iot',      label: 'IoT & Networking' },
  { id: 'training', label: 'Training' },
];

/* ── Bento grid (all) ─────────────────────────────────────── */
const BentoGrid = () => (
  <div className="dw-bento dw-bento--animate">
    <div className="dw-bento__cell dw-bento__cell--large">
      <img src={CAPABILITIES.ai.img} alt="AI" className="dw-bento__img" />
      <div className="dw-bento__overlay">
        <span className="dw-bento__eyebrow">Predictive Intel</span>
        <h3 className="dw-bento__title dw-bento__title--lg">{CAPABILITIES.ai.title}</h3>
        <p className="dw-bento__sub">Data-driven intelligence powering automation and decision systems.</p>
      </div>
    </div>

    <div className="dw-bento__cell dw-bento__cell--wide">
      <img src={CAPABILITIES.xr.img} alt="XR" className="dw-bento__img" />
      <div className="dw-bento__overlay">
        <h3 className="dw-bento__title">{CAPABILITIES.xr.title}</h3>
        <p className="dw-bento__micro">Visualization &amp; Interaction</p>
      </div>
    </div>

    <div className="dw-bento__cell">
      <img src={CAPABILITIES.iot.img} alt="IoT" className="dw-bento__img" />
      <div className="dw-bento__overlay">
        <h3 className="dw-bento__title dw-bento__title--sm">IoT</h3>
        <p className="dw-bento__micro">Infrastructure</p>
      </div>
    </div>

    <div className="dw-bento__cell">
      <img src={CAPABILITIES.dt.img} alt="Digital Twins" className="dw-bento__img" />
      <div className="dw-bento__overlay">
        <h3 className="dw-bento__title dw-bento__title--sm">Digital Twins</h3>
        <p className="dw-bento__micro">Simulation</p>
      </div>
    </div>

    <div className="dw-bento__cell dw-bento__cell--wide" style={{ position: 'relative', overflow: 'hidden' }}>
      <img src={cyberImg} alt="Cyber Security" className="dw-bento__img" />
      <div className="dw-bento__overlay">
        <h3 className="dw-bento__title">Cyber Security</h3>
        <p className="dw-bento__sub">Hardening industrial digital threads.</p>
      </div>
    </div>

    <div className="dw-bento__cell dw-bento__cell--wide" style={{ position: 'relative', overflow: 'hidden' }}>
      <img src={simulationImg} alt="Training & Simulation" className="dw-bento__img" />
      <div className="dw-bento__overlay">
        <h3 className="dw-bento__title">{CAPABILITIES.training.title}</h3>
        <p className="dw-bento__sub">Redefining skill acquisition through immersive VR safety environments.</p>
      </div>
    </div>
  </div>
);

/* ── Single capability detail ─────────────────────────────── */
const CapabilityDetail = ({ item }) => (
  <div className="dw-cap-detail">
    <Reveal animation="fade-right" style={{ clipPath: 'none', overflow: 'visible' }}>
      <div className="dw-cap-detail__img-wrap">
        <img src={item.img} alt={item.title} className="dw-cap-detail__img" />
      </div>
    </Reveal>
    <Reveal animation="fade-left" delay={120}>
      <div className="dw-cap-detail__content">
        <span className="dw-cap-detail__eyebrow">{item.label}</span>
        <h2 className="dw-cap-detail__heading">{item.title}</h2>
        <p className="dw-cap-detail__body">{item.desc}</p>
        <div className="dw-cap-detail__points">
          {item.points.map((p, i) => (
            <div key={p} className="dw-cap-detail__point" style={{ transitionDelay: `${i * 60}ms` }}>
              <div className="dw-cap-detail__check">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6 9 17l-5-5"/>
                </svg>
              </div>
              <span>{p}</span>
            </div>
          ))}
        </div>
      </div>
    </Reveal>
  </div>
);

/* ── Home ─────────────────────────────────────────────────── */
const PRODUCTS = [
  {
    id: 'fusionsite',
    title: 'FusionSite360XR',
    sub: 'Real-time site visualization',
    badge: 'XR Powered',
    badgeCls: 'dw-home__product-badge--blue',
    img: null, // set via xrImg below
    desc: [
      'Digital twin platform that utilizes extended reality (XR) to enhance site management and collaboration. With immersive visualizations and real-time data, it allows teams to remotely interact with project environments, improving communication and decision-making for industries like AECO, Manufacturing and Oil & Gas.',
      'By seamlessly bridging physical and digital realms, FusionSite 360XR simplifies tasks like inspections, design reviews, and presentations. Its intuitive design and data-driven insights streamline workflows, boost efficiency, and accelerate project timelines, making it a vital tool for managing complex projects.',
    ],
  },
  {
    id: 'holoxr',
    title: 'HoloXR',
    sub: 'Immersive content creation',
    badge: 'No-Code',
    badgeCls: 'dw-home__product-badge--purple',
    img: null,
    desc: [
      'No-code augmented reality (AR) content builder designed to revolutionize content creation across education, marketing, curated tours, and indoor navigation. With its intuitive drag-and-drop interface, HoloXR enables users to create immersive AR experiences without requiring technical expertise.',
      'It offers versatile tools to customize content for various industries, making AR more accessible and impactful. Whether enhancing user engagement or transforming storytelling, HoloXR is redefining how businesses and educators communicate through interactive, immersive experiences.',
    ],
  },
  {
    id: 'eduxr',
    title: 'EduXR',
    sub: 'Virtual campus learning',
    badge: 'Metaverse',
    badgeCls: 'dw-home__product-badge--green',
    img: null,
    desc: [
      'Metaverse platform transforming university education through immersive and interactive learning. By integrating VR, AR, and digital twins, it enables virtual campuses, dynamic lectures, and hands-on training, accessible from anywhere.',
      'Designed for collaboration and real-world simulation, EduXR bridges the gap between theory and practice. From virtual labs to industry training, it redefines education with innovation and engagement at its core.',
    ],
  },
];

const Home = () => {
  const navigate = useNavigate();
  const [activePill, setActivePill] = useState('all');
  const [activeCard, setActiveCard] = useState(null);

  return (
    <main className="dw-home">

      {/* ── HERO ──────────────────────────────────────────── */}
      <header className="dw-home__hero-wrap">
        <div className="dw-home__hero">

          {/* Hero content */}
          <div className="dw-home__hero-content">
            <span className="dw-home__hero-eyebrow">A Teczo Subsidiary</span>
            <h1 className="dw-home__hero-heading">DigitalWorks</h1>
            <p className="dw-home__hero-sub">
              Engineering Immersive Intelligence for the Digital Future. We design advanced AI, XR, and system solutions bridging the physical and digital worlds.
            </p>
            <div className="dw-home__hero-btns">
              <button className="dw-home__btn-primary" onClick={() => navigate('/services')}>
                Explore Our Solutions
              </button>
              <button className="dw-home__btn-outline" onClick={() => navigate('/contact')}>
                Start a Project
              </button>
            </div>
          </div>

          {/* Floating stat card bottom-left */}
          <div className="dw-home__stat-card dw-home__stat-card--bl">
            <div className="dw-home__glass-card dw-home__floating">
              <h3 className="dw-home__stat-val">RM 2.0M+</h3>
              <p className="dw-home__stat-label">Cost Savings Achieved</p>
              <p className="dw-home__stat-desc">Optimizing complex operations through intelligent digital twins.</p>
            </div>
            <div className="dw-home__hero-thumb dw-home__floating-delayed">
              <img src="https://images.unsplash.com/photo-1558346490-a72e53ae2d4f?w=400&auto=format&fit=crop" alt="Data visualization" />
            </div>
          </div>

          {/* Stats bottom-right */}
          <div className="dw-home__stats-br">
            <div className="dw-home__stats-row">
              <div className="dw-home__stat-item">
                <p className="dw-home__stat-num">75%</p>
                <p className="dw-home__stat-tag">Satisfaction</p>
              </div>
              <div className="dw-home__stat-divider" />
              <div className="dw-home__stat-item">
                <p className="dw-home__stat-num">15+</p>
                <p className="dw-home__stat-tag">Projects</p>
              </div>
            </div>
          </div>

        </div>
      </header>

      {/* ── MISSION & VIDEO ───────────────────────────────── */}
      <section className="dw-home__mission">
        <div className="dw-home__mission-inner">

          <Reveal animation="fade-right">
            <div className="dw-home__mission-left">
              <h2 className="dw-home__mission-heading">Engineering the Experience.</h2>
              <p className="dw-home__mission-body">We don't just build technology. We engineer intelligent experiences that transform how industries work.</p>
            </div>
          </Reveal>

          <Reveal animation="scale-up" delay={100} style={{ clipPath: 'none', overflow: 'visible' }}>
            <div className="dw-home__video-wrap">
              <video
                src={devVideo}
                autoPlay
                loop
                muted
                playsInline
                className="dw-home__video-img"
              />
            </div>
          </Reveal>

          <Reveal animation="fade-left" delay={200}>
            <p className="dw-home__mission-quote">
              We specialize in building immersive, intelligent, and scalable systems that enable organizations to{' '}
              <span className="dw-home__mission-pill">visualize</span>
              {' '}simulated environments and optimize real-world operations.
            </p>
          </Reveal>

        </div>
      </section>

      {/* ── CAPABILITIES ──────────────────────────────────── */}
      <section className="dw-home__caps-section">
        <div className="dw-home__caps-inner">

          <Reveal animation="fade-up">
            <div className="dw-home__caps-header">
              <div className="dw-home__caps-header-left">
                <h2 className="dw-home__caps-heading">Our Capabilities</h2>
                <p className="dw-home__caps-sub">
                  From AI-driven platforms to extended reality and digital twins, we turn complex industrial challenges into deployable solutions.
                </p>
              </div>
              <div className="dw-home__pills">
                {PILLS.map(pill => (
                  <button
                    key={pill.id}
                    className={`dw-home__pill ${activePill === pill.id ? 'dw-home__pill--active' : ''}`}
                    onClick={() => setActivePill(pill.id)}
                  >
                    {pill.label}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>

          <div className="dw-home__caps-grid">
            {activePill === 'all'
              ? <Reveal animation="fade-up" delay={50}><BentoGrid /></Reveal>
              : <CapabilityDetail item={CAPABILITIES[activePill]} />
            }
          </div>

        </div>
      </section>

      {/* ── FLAGSHIP SOLUTIONS ────────────────────────────── */}
      <section className="dw-home__flagship">
        <div className="dw-home__flagship-inner">
          <Reveal animation="fade-up">
            <div className="dw-home__flagship-header">
              <div>
                <h2 className="dw-home__flagship-heading">Flagship Solutions</h2>
                <p className="dw-home__flagship-sub-label">Ready to Deploy Systems</p>
              </div>
              <p className="dw-home__flagship-desc">
                Our proprietary platforms enable organizations to visualize, simulate, and optimize real-world operations in real-time digital environments.
              </p>
            </div>
          </Reveal>

          {/* Default 3-col grid */}
          {!activeCard && (
            <div className="dw-home__flagship-grid">
              {[
                { id: 'fusionsite', img: xrImg },
                { id: 'holoxr',    img: holoxrImg },
                { id: 'eduxr',     img: eduxrImg },
              ].map((item, i) => {
                const p = PRODUCTS.find(p => p.id === item.id);
                return (
                  <Reveal key={item.id} animation="fade-up" delay={i * 120} style={{ clipPath: 'none', overflow: 'visible' }}>
                    <div
                      className="dw-home__product-card"
                      onClick={() => setActiveCard(item.id)}
                    >
                      <div className="dw-home__product-img-wrap">
                        <span className={`dw-home__product-badge ${p.badgeCls}`}>{p.badge}</span>
                        <img src={item.img} alt={p.title} className="dw-home__product-img" />
                      </div>
                      <div className="dw-home__product-footer">
                        <div>
                          <h4 className="dw-home__product-title">{p.title}</h4>
                          <p className="dw-home__product-sub">{p.sub}</p>
                        </div>
                        <button className="dw-home__product-btn" onClick={e => { e.stopPropagation(); navigate('/services'); }}>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M7 17L17 7M17 7H7M17 7v10"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          )}

          {/* Split layout when a card is active */}
          {activeCard && (() => {
            const imgs = { fusionsite: xrImg, holoxr: holoxrImg, eduxr: eduxrImg };
            const p = PRODUCTS.find(p => p.id === activeCard);
            return (
              <div className="dw-home__flagship-split">
                {/* Left: card */}
                <div className="dw-home__split-card" style={{ clipPath: 'none', overflow: 'visible' }}>
                  <div className="dw-home__product-card dw-home__product-card--active">
                    <div className="dw-home__product-img-wrap">
                      <span className={`dw-home__product-badge ${p.badgeCls}`}>{p.badge}</span>
                      <img src={imgs[activeCard]} alt={p.title} className="dw-home__product-img" />
                    </div>
                    <div className="dw-home__product-footer">
                      <div>
                        <h4 className="dw-home__product-title">{p.title}</h4>
                        <p className="dw-home__product-sub">{p.sub}</p>
                      </div>
                      <button className="dw-home__product-btn" onClick={() => navigate('/services')}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M7 17L17 7M17 7H7M17 7v10"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right: description */}
                <div className="dw-home__split-desc">
                  <span className={`dw-home__product-badge ${p.badgeCls}`} style={{ position: 'static', marginBottom: '1.25rem', display: 'inline-block' }}>{p.badge}</span>
                  <h3 className="dw-home__split-title">{p.title}</h3>
                  <p className="dw-home__split-sub">{p.sub}</p>
                  <div className="dw-home__split-body">
                    {p.desc.map((para, j) => <p key={j}>{para}</p>)}
                  </div>
                  <button className="dw-home__split-back" onClick={() => setActiveCard(null)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 12H5M12 5l-7 7 7 7"/>
                    </svg>
                    Back to all solutions
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* ── INDUSTRIES ────────────────────────────────────── */}
      <section className="dw-home__industries">
        <div className="dw-home__industries-inner">

          <Reveal animation="fade-right">
            <div className="dw-home__industries-left">
              <h2 className="dw-home__industries-heading">Industries We Serve</h2>
              <p className="dw-home__industries-sub">We deliver sector-specific immersive intelligence that turns complex operational data into actionable, visual experiences.</p>
              <div className="dw-home__industries-stats">
                <div className="dw-home__ind-stat dw-home__ind-stat--dark">
                  <p className="dw-home__ind-stat-val">75%</p>
                  <p className="dw-home__ind-stat-label">Client Success Rate</p>
                </div>
                <div className="dw-home__ind-stat">
                  <p className="dw-home__ind-stat-val">5.0</p>
                  <p className="dw-home__ind-stat-label">Innovation Index</p>
                </div>
              </div>
            </div>
          </Reveal>

          <div className="dw-home__industries-right">
            <Reveal animation="fade-up" delay={0} style={{ clipPath: 'none', overflow: 'visible' }}>
              <div className="dw-home__ind-card">
                <div className="dw-home__ind-card-top">
                  <span className="dw-home__ind-card-title">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>
                    </svg>
                    Energy &amp; Oil &amp; Gas
                  </span>
                </div>
                <p className="dw-home__ind-card-body">Optimizing high-risk offshore and onshore operations through high-fidelity digital twins. We enable real-time asset monitoring and remote inspections.</p>
              </div>
            </Reveal>

            <Reveal animation="fade-up" delay={100} style={{ clipPath: 'none', overflow: 'visible' }}>
              <div className="dw-home__ind-card">
                <div className="dw-home__ind-card-top">
                  <span className="dw-home__ind-card-title">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2C97D4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 20h20"/><path d="M5 20V8l7-6 7 6v12"/>
                      <path d="M9 20v-5h6v5"/>
                    </svg>
                    Construction &amp; AECO
                  </span>
                </div>
                <p className="dw-home__ind-card-body">Bridging the gap between 2D blueprints and 3D reality. Our XR systems allow project managers to walk through digital twins in real-time.</p>
              </div>
            </Reveal>

            <Reveal animation="fade-up" delay={200} style={{ clipPath: 'none', overflow: 'visible' }}>
              <div className="dw-home__ind-card">
                <div className="dw-home__ind-card-top">
                  <span className="dw-home__ind-card-title">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                    </svg>
                    Healthcare
                  </span>
                </div>
                <p className="dw-home__ind-card-body">Revolutionizing medical training and patient outcomes through surgical simulations and immersive care platforms.</p>
              </div>
            </Reveal>
          </div>

        </div>
      </section>

      {/* ── FOOTER CTA ────────────────────────────────────── */}
      <div className="dw-home__footer-cta-wrap">
        <div className="dw-home__footer-cta">
          <Reveal animation="fade-up">
            <div className="dw-home__footer-cta-inner">
              <h2 className="dw-home__footer-cta-heading">Build the Future.</h2>
              <p className="dw-home__footer-cta-sub">
                From AI systems to XR-powered digital twins, DigitalWorks delivers the technology foundation for next-generation operations.
              </p>
              <button className="dw-home__footer-btn-primary" onClick={() => navigate('/contact')}>Contact Us</button>
            </div>
          </Reveal>
        </div>
      </div>

    </main>
  );
};

export default Home;
